// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

export interface TimerMetrics {
  startTime: number;
  pauseCount: number;
  resumeCount: number;
  totalPauseTime: number;
  totalActiveTime: number;
  errors: string[];
  performance: TimerPerformanceMetrics;
}

export interface TimerPerformanceMetrics {
  renderTime: number;
  updateTime: number;
  memoryUsage?: number;
}

export interface TimerSessionSummary {
  sessionId: string;
  totalTime: number;
  pauseCount: number;
  resumeCount: number;
  errorCount: number;
  performance: TimerPerformanceMetrics;
}

export interface AnalyticsEvent {
  event: string;
  properties: Record<string, any>;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const TIMER_METRICS_CONSTANTS = {
  PERFORMANCE: {
    POOR_PERFORMANCE_THRESHOLD: 16, // ms (60fps = ~16.67ms per frame)
    MAX_EVENTS: 100,
    MEMORY_CHECK_INTERVAL: 5000 // Check memory every 5 seconds
  },
  LOGGING: {
    EMOJI: {
      START: '📊',
      PAUSE: '⏸️',
      RESUME: '▶️',
      ERROR: '❌',
      PERFORMANCE: '⚡',
      END: '🏁',
      RESET: '🔄'
    },
    PREFIX: 'TimerMetrics'
  },
  ANALYTICS: {
    EVENT_NAME: 'timer_session_completed',
    WINDOW_ANALYTICS_PROP: 'analytics'
  }
} as const;

// ============================================================================
// TIMER METRICS TRACKER CLASS
// ============================================================================

/**
 * Comprehensive timer metrics tracking system for performance monitoring
 */
class TimerMetricsTracker {
  private metrics: Map<string, TimerMetrics> = new Map();
  private currentSessionId: string | null = null;
  private memoryCheckInterval: NodeJS.Timeout | null = null;

  constructor() {
    this.setupMemoryMonitoring();
  }

  // ============================================================================
  // SESSION MANAGEMENT
  // ============================================================================

  /**
   * Start tracking metrics for a new session
   * @param sessionId - Unique identifier for the session
   */
  startSession(sessionId: string): void {
    this.log('Starting session', sessionId, { sessionId });
    
    this.currentSessionId = sessionId;
    this.metrics.set(sessionId, this.createInitialMetrics());
    
    // Start memory monitoring for this session
    this.startMemoryMonitoring();
  }

  /**
   * End the current session and return final metrics
   * @returns Final metrics for the session or null if no session
   */
  endSession(): TimerSessionSummary | null {
    if (!this.currentSessionId) return null;
    
    const metrics = this.metrics.get(this.currentSessionId);
    if (!metrics) return null;

    const endTime = Date.now();
    const totalTime = endTime - metrics.startTime;
    
    const summary: TimerSessionSummary = {
      sessionId: this.currentSessionId,
      totalTime,
      pauseCount: metrics.pauseCount,
      resumeCount: metrics.resumeCount,
      errorCount: metrics.errors.length,
      performance: metrics.performance
    };

    this.log('Session ended', this.currentSessionId, summary);
    
    // Send analytics if available
    this.sendAnalytics(summary);
    
    // Cleanup
    this.stopMemoryMonitoring();
    this.currentSessionId = null;
    
    return summary;
  }

  /**
   * Reset all metrics and stop monitoring
   */
  reset(): void {
    this.metrics.clear();
    this.currentSessionId = null;
    this.stopMemoryMonitoring();
    this.log('Reset', null, { action: 'reset' });
  }

  // ============================================================================
  // METRICS TRACKING
  // ============================================================================

  /**
   * Record a pause event for the current session
   */
  pause(): void {
    if (!this.currentSessionId) return;
    
    const metrics = this.metrics.get(this.currentSessionId);
    if (metrics) {
      metrics.pauseCount++;
      this.log('Pause recorded', this.currentSessionId, { 
        pauseCount: metrics.pauseCount 
      });
    }
  }

  /**
   * Record a resume event for the current session
   */
  resume(): void {
    if (!this.currentSessionId) return;
    
    const metrics = this.metrics.get(this.currentSessionId);
    if (metrics) {
      metrics.resumeCount++;
      this.log('Resume recorded', this.currentSessionId, { 
        resumeCount: metrics.resumeCount 
      });
    }
  }

  /**
   * Record an error for the current session
   * @param error - Error message or description
   */
  recordError(error: string): void {
    if (!this.currentSessionId) return;
    
    const metrics = this.metrics.get(this.currentSessionId);
    if (metrics) {
      metrics.errors.push(error);
      this.log('Error recorded', this.currentSessionId, { 
        error, 
        totalErrors: metrics.errors.length 
      });
    }
  }

  /**
   * Record performance metrics for the current session
   * @param renderTime - Time taken for rendering in milliseconds
   * @param updateTime - Time taken for updates in milliseconds
   */
  recordPerformance(renderTime: number, updateTime: number): void {
    if (!this.currentSessionId) return;
    
    const metrics = this.metrics.get(this.currentSessionId);
    if (metrics) {
      metrics.performance.renderTime = renderTime;
      metrics.performance.updateTime = updateTime;
      
      // Check for performance issues
      this.checkPerformanceIssues(renderTime, updateTime);
      
      this.log('Performance recorded', this.currentSessionId, {
        renderTime,
        updateTime
      });
    }
  }

  // ============================================================================
  // QUERY METHODS
  // ============================================================================

  /**
   * Get metrics for the current session
   * @returns Current session metrics or null
   */
  getCurrentMetrics(): TimerMetrics | null {
    if (!this.currentSessionId) return null;
    return this.metrics.get(this.currentSessionId) || null;
  }

  /**
   * Get metrics for a specific session
   * @param sessionId - Session identifier
   * @returns Session metrics or null
   */
  getSessionMetrics(sessionId: string): TimerMetrics | null {
    return this.metrics.get(sessionId) || null;
  }

  /**
   * Get all tracked sessions
   * @returns Array of session IDs
   */
  getTrackedSessions(): string[] {
    return Array.from(this.metrics.keys());
  }

  /**
   * Get summary statistics across all sessions
   * @returns Summary of all sessions
   */
  getAllSessionsSummary(): {
    totalSessions: number;
    totalErrors: number;
    averagePerformance: {
      renderTime: number;
      updateTime: number;
    };
  } {
    const sessions = Array.from(this.metrics.values());
    const totalSessions = sessions.length;
    
    if (totalSessions === 0) {
      return {
        totalSessions: 0,
        totalErrors: 0,
        averagePerformance: { renderTime: 0, updateTime: 0 }
      };
    }

    const totalErrors = sessions.reduce((sum, session) => sum + session.errors.length, 0);
    const totalRenderTime = sessions.reduce((sum, session) => sum + session.performance.renderTime, 0);
    const totalUpdateTime = sessions.reduce((sum, session) => sum + session.performance.updateTime, 0);

    return {
      totalSessions,
      totalErrors,
      averagePerformance: {
        renderTime: totalRenderTime / totalSessions,
        updateTime: totalUpdateTime / totalSessions
      }
    };
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  /**
   * Create initial metrics object for a new session
   */
  private createInitialMetrics(): TimerMetrics {
    return {
      startTime: Date.now(),
      pauseCount: 0,
      resumeCount: 0,
      totalPauseTime: 0,
      totalActiveTime: 0,
      errors: [],
      performance: {
        renderTime: 0,
        updateTime: 0
      }
    };
  }

  /**
   * Log metrics events with consistent formatting
   */
  private log(action: string, sessionId: string | null, details: Record<string, any>): void {
    const emoji = this.getLogEmoji(action);
    const prefix = TIMER_METRICS_CONSTANTS.LOGGING.PREFIX;
    
    console.log(`${emoji} ${prefix}: ${action}`, {
      sessionId,
      ...details,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Get appropriate emoji for logging
   */
  private getLogEmoji(action: string): string {
    const emojiMap: Record<string, string> = {
      'Starting session': TIMER_METRICS_CONSTANTS.LOGGING.EMOJI.START,
      'Pause recorded': TIMER_METRICS_CONSTANTS.LOGGING.EMOJI.PAUSE,
      'Resume recorded': TIMER_METRICS_CONSTANTS.LOGGING.EMOJI.RESUME,
      'Error recorded': TIMER_METRICS_CONSTANTS.LOGGING.EMOJI.ERROR,
      'Performance recorded': TIMER_METRICS_CONSTANTS.LOGGING.EMOJI.PERFORMANCE,
      'Session ended': TIMER_METRICS_CONSTANTS.LOGGING.EMOJI.END,
      'Reset': TIMER_METRICS_CONSTANTS.LOGGING.EMOJI.RESET
    };

    return emojiMap[action] || TIMER_METRICS_CONSTANTS.LOGGING.EMOJI.START;
  }

  /**
   * Check for performance issues and log warnings
   */
  private checkPerformanceIssues(renderTime: number, updateTime: number): void {
    const threshold = TIMER_METRICS_CONSTANTS.PERFORMANCE.POOR_PERFORMANCE_THRESHOLD;
    
    if (renderTime > threshold || updateTime > threshold) {
      console.warn('⚠️ TimerMetrics: Poor performance detected', {
        renderTime,
        updateTime,
        threshold,
        sessionId: this.currentSessionId
      });
    }
  }

  /**
   * Send analytics data if available
   */
  private sendAnalytics(summary: TimerSessionSummary): void {
    if (typeof window === 'undefined') return;

    const analytics = (window as any)[TIMER_METRICS_CONSTANTS.ANALYTICS.WINDOW_ANALYTICS_PROP];
    
    if (analytics && typeof analytics.track === 'function') {
      try {
        analytics.track(TIMER_METRICS_CONSTANTS.ANALYTICS.EVENT_NAME, {
          sessionId: summary.sessionId,
          totalTime: summary.totalTime,
          pauseCount: summary.pauseCount,
          resumeCount: summary.resumeCount,
          errorCount: summary.errorCount,
          performance: summary.performance
        });
      } catch (error) {
        console.warn('Failed to send analytics:', error);
      }
    }
  }

  // ============================================================================
  // MEMORY MONITORING
  // ============================================================================

  /**
   * Setup memory monitoring for the tracker
   */
  private setupMemoryMonitoring(): void {
    if (typeof window === 'undefined') return;
    
    // Check if performance.memory is available (Chrome only)
    if ('memory' in performance) {
      this.startMemoryMonitoring();
    }
  }

  /**
   * Start monitoring memory usage
   */
  private startMemoryMonitoring(): void {
    if (this.memoryCheckInterval) return;

    this.memoryCheckInterval = setInterval(() => {
      this.updateMemoryUsage();
    }, TIMER_METRICS_CONSTANTS.PERFORMANCE.MEMORY_CHECK_INTERVAL);
  }

  /**
   * Stop monitoring memory usage
   */
  private stopMemoryMonitoring(): void {
    if (this.memoryCheckInterval) {
      clearInterval(this.memoryCheckInterval);
      this.memoryCheckInterval = null;
    }
  }

  /**
   * Update memory usage for current session
   */
  private updateMemoryUsage(): void {
    if (!this.currentSessionId || typeof window === 'undefined') return;
    
    const memory = (performance as any).memory;
    if (memory) {
      const metrics = this.metrics.get(this.currentSessionId);
      if (metrics) {
        metrics.performance.memoryUsage = memory.usedJSHeapSize;
      }
    }
  }
}

// ============================================================================
// EXPORTED INSTANCE
// ============================================================================

/**
 * Global timer metrics tracker instance
 */
export const timerMetrics = new TimerMetricsTracker();

// ============================================================================
// CONVENIENCE FUNCTIONS
// ============================================================================

/**
 * Quick function to record a timer error
 * @param error - Error message
 * @param sessionId - Optional session ID (uses current if not provided)
 */
export const recordTimerError = (error: string, sessionId?: string): void => {
  if (sessionId && sessionId !== timerMetrics.getCurrentMetrics()?.startTime.toString()) {
    // Create a temporary session for this error
    const tempMetrics = new TimerMetricsTracker();
    tempMetrics.startSession(sessionId);
    tempMetrics.recordError(error);
    tempMetrics.endSession();
  } else {
    timerMetrics.recordError(error);
  }
};

/**
 * Quick function to record performance metrics
 * @param renderTime - Render time in milliseconds
 * @param updateTime - Update time in milliseconds
 */
export const recordTimerPerformance = (renderTime: number, updateTime: number): void => {
  timerMetrics.recordPerformance(renderTime, updateTime);
}; 